import { useId } from 'react';
import { GuennieIcon } from './GuennieIcon';
import { GuennieEisBild } from './GuennieEisBild';

const STUFEN_FARBE = [
  'var(--guennie-1)',
  'var(--guennie-2)',
  'var(--guennie-3)',
  'var(--guennie-4)',
  'var(--guennie-5)',
];

const BEZEICHNER: Record<number, string> = {
  1: 'durchgefallen',
  2: 'geht besser',
  3: 'solide',
  4: 'hervorragend',
  5: 'Referenzklasse',
};

const GROESSEN = { sm: 18, md: 26, lg: 40 } as const;
/** Breite der Illustration im Anzeigemodus (siehe GuennieEisBild) je Größe. */
const BILD_GROESSEN = { sm: 28, md: 40, lg: 64 } as const;
/** Breite des Bewertungsbalkens je Größe. */
const BALKEN_BREITEN = { sm: 34, md: 48, lg: 76 } as const;

/** Bewertungsstufe als Fortschrittsbalken statt eingefärbtem Icon (siehe GuennieRating, Anzeigemodus). */
function GuennieBalken({ anteil, farbe, breite }: { anteil: number; farbe: string; breite: number }) {
  const gefuellt = Math.max(0, Math.min(1, anteil));
  return (
    <svg width={breite} height={8} viewBox={`0 0 ${breite} 8`} role="presentation" aria-hidden="true">
      <rect x="0.75" y="0.75" width={breite - 1.5} height="6.5" rx="3.25" fill="none" stroke="var(--raspel)" strokeWidth="1.5" />
      {gefuellt > 0 ? (
        <rect x="0.75" y="0.75" width={Math.max(0, gefuellt * (breite - 1.5))} height="6.5" rx="3.25" fill={farbe} />
      ) : null}
    </svg>
  );
}

/** Dieselbe Stufenfarbe wie in der Bewertungsanzeige — für Kartenmarker (Bauphase 6). */
export function farbeFuerGuennies(wert: number | null | undefined): string {
  const w = typeof wert === 'number' ? wert : 0;
  if (w <= 0) return 'var(--guennie-leer)';
  const gerundet = Math.round(w);
  return STUFEN_FARBE[Math.min(4, Math.max(0, gerundet - 1))];
}

export interface GuennieRatingProps {
  /** aktueller Wert (1..5, oder Dezimalwert für Durchschnitt) */
  wert: number | null | undefined;
  /** Eingabemodus? sonst nur Anzeige */
  eingabe?: boolean;
  onChange?: (wert: number) => void;
  groesse?: keyof typeof GROESSEN;
  label?: string;
  /** Zahl neben den Icons zeigen */
  mitZahl?: boolean;
}

/**
 * Die einzige Bewertungskomponente (siehe technical-design.md 3 / 4.4).
 * - Einzelkriterien: eingabe=true, ganze Werte 1..5
 * - Gesamtbewertung / Durchschnitte: eingabe=false, anteilig gefülltes Icon + Zahl
 */
export function GuennieRating({
  wert,
  eingabe = false,
  onChange,
  groesse = 'md',
  label,
  mitZahl = true,
}: GuennieRatingProps) {
  const gruppeId = useId();
  const px = GROESSEN[groesse];
  const w = typeof wert === 'number' ? wert : 0;
  const gerundet = Math.round(w);
  const farbe = w > 0 ? STUFEN_FARBE[Math.min(4, Math.max(0, gerundet - 1))] : 'var(--guennie-leer)';

  if (eingabe) {
    return (
      <div
        role="radiogroup"
        aria-label={label ?? 'Bewertung in Günnies'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const aktiv = n <= gerundet;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={n === gerundet}
              aria-label={`${n} von 5 Günnies – ${BEZEICHNER[n]}`}
              id={`${gruppeId}-${n}`}
              onClick={() => onChange?.(n)}
              style={{
                background: 'none',
                border: 'none',
                padding: 2,
                cursor: 'pointer',
                lineHeight: 0,
              }}
            >
              <GuennieIcon
                gefuellt={aktiv}
                groesse={px}
                farbe={aktiv ? STUFEN_FARBE[n - 1] : 'var(--guennie-leer)'}
              />
            </button>
          );
        })}
        {mitZahl && gerundet > 0 ? (
          <span className="tabellenziffern" style={{ marginLeft: '0.4rem', color: 'var(--espresso-70)' }}>
            {gerundet} · {BEZEICHNER[gerundet]}
          </span>
        ) : null}
      </div>
    );
  }

  // Anzeigemodus (auch Dezimalwerte): Illustration + Bewertungsbalken statt
  // fünf einzeln eingefärbter Icons — die Illustration ist naturgetreu-farbig
  // und kann die Stufe nicht mehr selbst per Einfärbung zeigen (siehe
  // GuennieEisBild).
  const titel = typeof wert === 'number' ? `${wert.toFixed(1).replace('.', ',')} von 5 Günnies` : 'noch nicht bewertet';
  return (
    <span
      title={titel}
      aria-label={titel}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
    >
      <GuennieEisBild groesse={BILD_GROESSEN[groesse]} />
      <GuennieBalken anteil={w / 5} farbe={farbe} breite={BALKEN_BREITEN[groesse]} />
      {mitZahl && typeof wert === 'number' ? (
        <strong className="tabellenziffern" style={{ marginLeft: '0.1rem' }}>
          {wert.toFixed(1).replace('.', ',')}
        </strong>
      ) : null}
    </span>
  );
}
